'use client';

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { useUsernameValidation, useDisplayNameValidation } from '@/hooks/useUsernameValidation';
import { cn } from '@/lib/utils';

interface UsernameInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  showSuggestions?: boolean;
  checkAvailability?: boolean;
}

export function UsernameInput({
  value,
  onChange,
  onValidationChange,
  label = 'Username',
  placeholder = 'Enter your username',
  className,
  showSuggestions = true,
  checkAvailability = true
}: UsernameInputProps) {
  const {
    username,
    setUsername,
    isValid,
    isChecking,
    error,
    suggestions
  } = useUsernameValidation({ checkAvailability });

  const [showSuggestionsList, setShowSuggestionsList] = useState(false);

  // Sync with parent component
  useState(() => {
    if (value !== username) {
      setUsername(value);
    }
  });

  const handleChange = (newValue: string) => {
    setUsername(newValue);
    onChange(newValue);
    onValidationChange?.(isValid);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleChange(suggestion);
    setShowSuggestionsList(false);
  };

  const getStatusIcon = () => {
    if (isChecking) {
      return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
    }
    
    if (username && !isChecking) {
      if (isValid) {
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      } else {
        return <XCircle className="w-4 h-4 text-red-500" />;
      }
    }
    
    return null;
  };

  const getStatusColor = () => {
    if (isChecking) return 'border-blue-500';
    if (username && !isChecking) {
      return isValid ? 'border-green-500' : 'border-red-500';
    }
    return 'border-gray-300';
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor="username">{label}</Label>
      
      <div className="relative">
        <div className="relative">
          <Input
            id="username"
            type="text"
            value={username}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => setShowSuggestionsList(true)}
            onBlur={() => setTimeout(() => setShowSuggestionsList(false), 200)}
            placeholder={placeholder}
            className={cn(
              'pr-10',
              getStatusColor()
            )}
            disabled={isChecking}
          />
          
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {getStatusIcon()}
          </div>
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && showSuggestionsList && suggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
            {suggestions.map((suggestion: string, index: number) => (
              <button
                key={index}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Success Message */}
      {isValid && username && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Username is available!
          </AlertDescription>
        </Alert>
      )}

      {/* Username Requirements */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>Username requirements:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>3-30 characters long</li>
          <li>Letters, numbers, underscores, hyphens, and periods only</li>
          <li>No consecutive special characters</li>
          <li>Cannot start or end with special characters</li>
          <li>Must be unique</li>
        </ul>
      </div>
    </div>
  );
}

interface DisplayNameInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

export function DisplayNameInput({
  value,
  onChange,
  onValidationChange,
  label = 'Display Name',
  placeholder = 'Enter your display name',
  className
}: DisplayNameInputProps) {
  const {
    displayName,
    setDisplayName,
    isValid,
    error,
    suggestions
  } = useDisplayNameValidation();

  const [showSuggestionsList, setShowSuggestionsList] = useState(false);

  // Sync with parent component
  useState(() => {
    if (value !== displayName) {
      setDisplayName(value);
    }
  });

  const handleChange = (newValue: string) => {
    setDisplayName(newValue);
    onChange(newValue);
    onValidationChange?.(isValid);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleChange(suggestion);
    setShowSuggestionsList(false);
  };

  const getStatusIcon = () => {
    if (displayName) {
      if (isValid) {
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      } else {
        return <XCircle className="w-4 h-4 text-red-500" />;
      }
    }
    return null;
  };

  const getStatusColor = () => {
    if (displayName) {
      return isValid ? 'border-green-500' : 'border-red-500';
    }
    return 'border-gray-300';
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor="displayName">{label}</Label>
      
      <div className="relative">
        <div className="relative">
          <Input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => setShowSuggestionsList(true)}
            onBlur={() => setTimeout(() => setShowSuggestionsList(false), 200)}
            placeholder={placeholder}
            className={cn(
              'pr-10',
              getStatusColor()
            )}
          />
          
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {getStatusIcon()}
          </div>
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestionsList && suggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
            {suggestions.map((suggestion: string, index: number) => (
              <button
                key={index}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Display Name Requirements */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>Display name requirements:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>2-50 characters long</li>
          <li>Can contain letters, numbers, and most special characters</li>
          <li>Cannot contain inappropriate content</li>
          <li>Cannot have excessive special characters</li>
        </ul>
      </div>
    </div>
  );
}
